// Backlog 072-C — OS-level desktop notification on plug terminal state.
//
// Best-effort cross-platform notifier: macOS uses `osascript`, Linux uses
// `notify-send`. The notifier MUST handle absence gracefully — no crash, no
// thrown error, no console noise — when the binary is missing, the user
// lacks permissions, or the OS is unsupported.
//
// All shell-outs go through the `notifyAdapter` seam so tests can inject a
// mock and never touch the real osascript/notify-send.

export interface OsNotifyInput {
  title: string;
  message: string;
}

export interface OsNotifyDeps {
  /**
   * Override the platform detection. Defaults to `process.platform`. Tests
   * pass 'darwin' / 'linux' / 'win32' to exercise each branch independently.
   */
  platform?: NodeJS.Platform;
  /**
   * Spawn adapter — called once per notification with the binary and args
   * we'd otherwise have shelled out to. Resolves to true on success, false
   * on any failure. The default adapter shells out via child_process.
   */
  spawn?: (cmd: string, args: string[]) => Promise<boolean>;
}

/**
 * Default spawn adapter — shells out to the named binary, ignoring stdout/
 * stderr. Resolves true if the process exited 0; false on any other outcome
 * (missing binary, non-zero exit, spawn error, signal).
 */
const defaultSpawn = async (cmd: string, args: string[]): Promise<boolean> => {
  const { spawn } = await import('node:child_process');
  return new Promise<boolean>((resolve) => {
    let resolved = false;
    let proc: ReturnType<typeof spawn>;
    try {
      proc = spawn(cmd, args, { stdio: 'ignore' });
    } catch {
      resolve(false);
      return;
    }
    const settle = (ok: boolean) => {
      if (resolved) return;
      resolved = true;
      resolve(ok);
    };
    proc.on('error', () => settle(false));
    proc.on('close', (code) => settle(code === 0));
  });
};

/**
 * AppleScript escaping: backslash and double-quote are the only metacharacters
 * we need to escape for `display notification "<msg>" with title "<title>"`.
 * Newlines render fine inside the literal string.
 */
function escapeAppleScript(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Fire an OS-level desktop notification. Resolves false silently when the
 * platform is unsupported or the underlying binary fails. Never throws.
 */
export async function osNotify(
  input: OsNotifyInput,
  deps: OsNotifyDeps = {},
): Promise<boolean> {
  const platform = deps.platform ?? process.platform;
  const spawn = deps.spawn ?? defaultSpawn;

  // Wrap the adapter call so a misbehaving spawn override (or a future bug
  // in the default) can never propagate out of osNotify. The contract is
  // that this function never throws — callers in the success-or-failure
  // path of `cloudgrid plug` rely on it.
  const safeSpawn = async (cmd: string, args: string[]): Promise<boolean> => {
    try {
      return await spawn(cmd, args);
    } catch {
      return false;
    }
  };

  if (platform === 'darwin') {
    const script = `display notification "${escapeAppleScript(
      input.message,
    )}" with title "${escapeAppleScript(input.title)}"`;
    return safeSpawn('osascript', ['-e', script]);
  }

  if (platform === 'linux') {
    return safeSpawn('notify-send', ['-i', 'info', input.title, input.message]);
  }

  // win32 / android / other — no native notifier wired up. Skip silently;
  // the deploys.log entry (072-D) is the durable record.
  return false;
}
